import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import * as moment from 'moment';
import axios from 'axios';
import translate from '../../../commonComponents/translate';
import Anchor from '../../../commonComponents/Anchor';
import ShopPriceEstimation from '../shopItems/ShopPriceEstimation';
import CartStateProp from '../propTypes/cartStateProp';
import UserCurrencyProp from '../../../commonComponents/propTypes/userCurrencyProp';
import { FREQUENCIES_TYPES } from '../../../services/utilityFunctions';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';
import Notifications from '../../../services/notifications';
import ShopAvailableCoupon from '../ShopAvailableCoupon';
import orderPriceProp from '../propTypes/orderPriceProp';
import MarketInfosContext from '../../../contextProviders/MarketInfosContext';
import ProductCheckoutItem from './ProductCheckoutItem';
import { getProductsForOrder } from '../shopHelper';

function ShopProductCheckoutList({
  t,
  cartState,
  userCurrency,
  goToShipping,
  updatedAmounts,
  resetAmounts,
  updateCartAmount,
  deleteProduct,
  updateCartCoupon,
  _csrf,
  orderDetails,
  productsAvailability,
  orderAlreadyCreated,
  directSellingActive,
  isSubscription,
  onUpdateProductFrequency,
}) {
  const [stockManagement, setStockManagement] = useState({});

  const { loggedUser } = useContext(LoggedUserContext);
  const { marketInfos } = useContext(MarketInfosContext);
  let userCountry = marketInfos.localCountry;
  if (loggedUser) {
    if (loggedUser.address && loggedUser.address.countryCode) {
      userCountry = loggedUser.address.countryCode;
    }
    if (loggedUser.shippingCountry) {
      userCountry = loggedUser.shippingCountry;
    }
  }

  const [coupons, setCoupons] = useState(null);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [textCoupon, setTextCoupon] = useState('');

  const handleAvailableCouponChange = data => {
    setCoupons(data);
  };

  const handleCouponClick = coupon => {
    // Make sure that we unselect the coupon if code is equal to the selected coupon
    if (
      coupon &&
      coupon.code === selectedCoupon &&
      cartState?.coupon?.code === coupon.code
    ) {
      setSelectedCoupon(null);
      updateCartCoupon(null);
    } else {
      setSelectedCoupon(coupon.code);
      updateCartCoupon(coupon.code);
    }
    setTextCoupon('');
  };

  const handleCouponCheck = () => {
    setSelectedCoupon('');
    updateCartCoupon(textCoupon);
  };

  useEffect(() => {
    if (cartState.products.length > 0 && loggedUser) {
      let availableCouponsReqPath =
        '/api/discount/coupon/available?moduleType=marketPlace';
      if (userCurrency) {
        availableCouponsReqPath += `&currency=${userCurrency.code}`;
      }
      if (userCountry) {
        availableCouponsReqPath += `&country=${userCountry}`;
      }
      axios
        .post(
          availableCouponsReqPath,
          {
            marketProductVariations: getProductsForOrder(
              cartState?.products,
              true,
            ),
          },
          {
            headers: {
              'Accept-Version': '1.0.0',
              'X-Gamification-API': 'Games',
              'X-CSRF-Token': _csrf,
              _csrf,
            },
          },
        )
        .then(({ data: response }) => {
          handleAvailableCouponChange(response.data);
        })
        .catch(err => {
          Notifications.showErrorFromRequest(err, 'error_getAvailableCoupons');
          console.log(err);
        });
    }
  }, [cartState.products]);

  const stockManagementMessage = variation => {
    const productItem = variation;
    const { isPack, packVariations = [] } = productItem;
    let hasExceededStock = false;
    if (isPack) {
      packVariations.forEach(packVariation => {
        const variationId = packVariation?.variation;
        const stockForVariation = stockManagement[variationId];
        if (
          stockForVariation &&
          stockForVariation.stockQuantity &&
          stockForVariation.stockQuantity < stockForVariation.quantityInCheckout
        ) {
          hasExceededStock = true;
        }
      });
    } else {
      const stockForVariation = stockManagement[variation.id];
      if (
        stockForVariation &&
        stockForVariation.stockQuantity &&
        stockForVariation.stockQuantity < stockForVariation.quantityInCheckout
      ) {
        hasExceededStock = true;
      }
    }
    // only show msgs when the stock is managed by us, not null
    if (productItem.stockQuantity !== null || hasExceededStock) {
      const { restockDate, stockQuantity, restockInterval, lowStockWarning } =
        productItem;
      let message = '';
      let date = null;
      if (restockDate) {
        date =
          new Date().getTime() > new Date(restockDate).getTime()
            ? null
            : moment.utc(restockDate).format('DD MMM, YY');
      }
      if (stockQuantity === 0) {
        const restockIntervalMsg = restockInterval
          ? t('shop.product.warning.message.noStock.interval', restockInterval)
          : t('shop.product.warning.message.noStock');
        message = date
          ? t('shop.product.warning.message.noStock.date', date)
          : restockIntervalMsg;
        return (
          <span
            className={
              date || restockInterval ? 'warning-yellow' : 'warning-red'
            }
          >
            {message}
          </span>
        );
      }
      if (stockQuantity < lowStockWarning) {
        message = t('shop.product.warning.message.lowStock');
      }
      if (stockQuantity < productItem.amount || hasExceededStock) {
        const restockIntervalMsg = restockInterval
          ? t(
              'shop.product.warning.message.quantityAboveStock.interval',
              restockInterval,
            )
          : t('shop.product.warning.message.warning.noStock');
        // t('shop.product.warning.message.quantityAboveStock.noDateNoInterval');
        message = date
          ? t('shop.product.warning.message.quantityAboveStock.date', date)
          : restockIntervalMsg;
      }
      return (
        <span
          className={
            !date && !restockInterval ? 'warning-red' : 'warning-yellow'
          }
        >
          {message}
        </span>
      );
    }
    return <span />;
  };

  // check if amount is greater than stock quantity, if stock is null, then return false;
  const productsAmountAboveQuantity = Object.values(stockManagement).filter(
    stock =>
      stock.stockQuantity && stock.stockQuantity < stock.quantityInCheckout,
  );

  // clear coupon selected
  const handleClearCoupon = () => {
    setSelectedCoupon(null);
    updateCartCoupon(null);
    setTextCoupon('');
  };

  const title = !isSubscription
    ? t('shop.cart.products')
    : t('shop.cart.subscriptions');
  /**
   * Set the stockManagement state to handle the quantity in checkout for each variation
   */
  const getStockForVariations = () => {
    const stocksForCheckout = {};

    // eslint-disable-next-line no-unused-expressions
    cartState?.products?.forEach(product => {
      const { isPack, packVariations } = product;

      if (isPack) {
        packVariations.forEach(({ variation, quantity, stockQuantity }) => {
          const key = variation;
          const amount = quantity * product?.amount;

          stocksForCheckout[key] = {
            stockQuantity,
            quantityInCheckout:
              (stocksForCheckout[key]?.quantityInCheckout || 0) + amount,
          };
        });
      } else {
        const key = product?.id;
        const stockQuantity = product?.stockQuantity;
        const amount = product?.amount;

        stocksForCheckout[key] = {
          stockQuantity,
          quantityInCheckout:
            (stocksForCheckout[key]?.quantityInCheckout || 0) + amount,
        };
      }
    });
    setStockManagement(stocksForCheckout);
  };

  // responsible to manage stocks in cart
  useEffect(() => {
    getStockForVariations();
  }, [cartState.products]);

  return (
    <div className="m-portlet--rounded m-portlet--rounded-force m--margin-top-10 m--margin-bottom-10 shop-checkout-step1">
      <div className="m-portlet__body">
        <div className="row no-padding">
          <div className="col-lg-8 col-md-8">
            <div className="row">
              <div className="col-12">
                <div className="all-products">
                  <h2>{title}</h2>
                  <div className="list-products">
                    <ul>
                      {cartState.products?.map(product => (
                        <ProductCheckoutItem
                          key={product.id}
                          product={product}
                          onRemoveProduct={deleteProduct}
                          onUpdateCartAmount={updateCartAmount}
                          cartState={cartState}
                          productsAvailability={productsAvailability}
                          getStockManagementMsg={stockManagementMessage}
                          onUpdateProductFrequency={onUpdateProductFrequency}
                        />
                      ))}
                    </ul>
                    {cartState.showUpdateCart && (
                      <div className="products-amounts-control">
                        <button
                          className="btn m-btn btn-dynamic"
                          type="button"
                          onClick={updatedAmounts}
                        >
                          {t('shop.cart.update')}
                        </button>
                        <button
                          className="btn m-btn btn-danger"
                          type="button"
                          onClick={resetAmounts}
                        >
                          {t('shop.cart.reset')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-12">
                <div className="coupons-choose">
                  <div className="title">
                    <h3>{t('shop.cart.apply_discount')}</h3>
                    <p>{t('shop.cart.choose_coupon_reward')}</p>
                  </div>
                  <div className="container">
                    <div className="row">
                      {coupons &&
                        coupons.map(coupon => (
                          <ShopAvailableCoupon
                            key={coupon._id}
                            coupon={coupon}
                            onClick={handleCouponClick}
                            selectedCoupon={selectedCoupon}
                            orderAlreadyCreated={orderAlreadyCreated}
                          />
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-4 col-md-4">
            <ShopPriceEstimation
              t={t}
              cartState={cartState}
              orderDetails={orderDetails}
              onClearCoupon={handleClearCoupon}
              directSellingActive={directSellingActive}
              showClearCoupon
            />
            <div className="check-coupon">
              <h4>{t('shop.cart.coupon')}</h4>
              <div className="form-group">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t('shop.cart.check_coupon_placeholder')}
                    value={textCoupon}
                    onChange={e => {
                      setTextCoupon(e.target.value);
                    }}
                    disabled={orderAlreadyCreated}
                  />
                  <div className="input-group-append">
                    <button
                      className="btn btn-theme btn-dynamic"
                      type="button"
                      disabled={textCoupon.length === 0 || orderAlreadyCreated}
                      onClick={handleCouponCheck}
                    >
                      {t('shop.cart.check')}
                    </button>
                  </div>
                  <span className="m-form__help">
                    {t('shop.cart.check_coupon_hint')}
                  </span>
                </div>
              </div>
            </div>
            {cartState.products.length > 0 &&
              !cartState.showUpdateCart &&
              !productsAmountAboveQuantity.length > 0 && (
                <Anchor
                  className="landing-certification-browse btn m-btn--pill btn-dynamic"
                  onClick={goToShipping}
                  href={loggedUser ? null : '/shop/checkout'}
                >
                  {t('shop.cart.proceed_to_checkout')}
                </Anchor>
              )}
            {productsAmountAboveQuantity.length > 0 && (
              <div className="check-coupon error-msg-checkout">
                {t('shop.product.warning.message.blockCheckout')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

ShopProductCheckoutList.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  cartState: PropTypes.shape(CartStateProp.propType).isRequired,
  userCurrency: PropTypes.shape(UserCurrencyProp.propType).isRequired,
  goToShipping: PropTypes.func.isRequired,
  updateCartCoupon: PropTypes.func.isRequired,
  updatedAmounts: PropTypes.func.isRequired,
  resetAmounts: PropTypes.func.isRequired,
  updateCartAmount: PropTypes.func.isRequired,
  deleteProduct: PropTypes.func.isRequired,
  orderDetails: PropTypes.shape(orderPriceProp.propType),
  productsAvailability: PropTypes.arrayOf(PropTypes.string),
  orderAlreadyCreated: PropTypes.string,
  directSellingActive: PropTypes.bool.isRequired,
  isSubscription: PropTypes.oneOf(FREQUENCIES_TYPES),
  onUpdateProductFrequency: PropTypes.func.isRequired,
};

ShopProductCheckoutList.defaultProps = {
  orderDetails: PropTypes.shape(orderPriceProp.defaultProp),
  productsAvailability: [],
  orderAlreadyCreated: null,
  isSubscription: null,
};

export default translate(ShopProductCheckoutList);
